'use client';

import { useEffect, useState } from 'react';

/**
 * Synchronous, Stripe-free Apple Pay device check.
 *
 * Returns whether the browser can present the Apple Pay sheet — the same Layer-1
 * probe {@link useApplePayAvailability} runs before its (optional) Stripe probe.
 * Uses the GLOBAL `ApplePaySession` (declared by `@types/applepayjs` everywhere)
 * rather than `window.ApplePaySession`, whose Window augmentation isn't visible
 * when this module is typechecked outside `libs/features`.
 *
 * `canMakePayments()` reports device/browser capability only (not whether an
 * active card is provisioned — that's the async `canMakePaymentsWithActiveCard`);
 * a card-less device still gets the button and Apple's sheet prompts to add one.
 *
 * `canMakePayments()` THROWS `InvalidAccessError` when called from an insecure
 * document (e.g. dev over plain `http://localhost`), and can throw in other
 * hostile environments — so the call is wrapped and any throw is treated as
 * "not supported" rather than crashing the render.
 */
export function isApplePayDeviceSupported(): boolean {
  if (typeof window === 'undefined' || typeof ApplePaySession === 'undefined')
    return false;
  try {
    return ApplePaySession.canMakePayments();
  } catch (error) {
    console.error('Apple Pay canMakePayments() probe failed:', error);
    return false;
  }
}

/**
 * Post-hydration Apple Pay device support. Starts `false` on the server and the
 * first client render (so SSR markup matches), then flips to the real value after
 * mount — a definite gate for deciding whether to mount the heavier express
 * checkout subtree, without a hydration mismatch.
 */
export function useApplePayDeviceSupported(): boolean {
  const [supported, setSupported] = useState(false);
  useEffect(() => {
    setSupported(isApplePayDeviceSupported());
  }, []);
  return supported;
}
